import React, { FC } from 'react';
import { Form, FormGroup, Input, Label } from 'reactstrap';
import { Button } from '@bphxd/ds-core-react';
import { EMPTY_LEAK_TEST_PACK } from '../../../lib/constants';
import { TLeakTestPack } from '../../../lib/types';
import { useSelectedAsset } from '../../../lib/util';
import { renderOptions } from '../../../lib/util/render';
import {
  LEAK_TEST_PACK_REVIEW_ENUM,
  LEAK_TEST_PACK_STATUS_ENUM,
  LEAK_TEST_PACK_TYPE_ENUM,
} from '../../../lib/constants/dbConstants';
import {
  LeakTestPackReviewEnum,
  LeakTestPackStatusEnum,
  LeakTestPackTypeEnum,
} from '../../../graph-queries/__generated__/graphql';

const LeakTestPackForm: FC<{
  defaultValues?: TLeakTestPack;
  onSubmit: (leakTestPack: TLeakTestPack) => Promise<unknown>;
  onClose: () => void;
  type: 'add' | 'edit';
  isLoading?: boolean;
}> = ({ defaultValues, onSubmit, type, onClose, isLoading }) => {
  const asset = useSelectedAsset();
  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    // Prevent default behavior
    event.preventDefault();

    const data = new FormData(event.target as HTMLFormElement);
    const inputData = Object.fromEntries(data) as Record<string, string> & {
      leakTestPackType: LeakTestPackTypeEnum;
      opsReview: LeakTestPackReviewEnum;
      engineeringReview: LeakTestPackReviewEnum;
      assetReview: LeakTestPackReviewEnum;
      status: LeakTestPackStatusEnum;
    };

    const leakTestPack: TLeakTestPack = {
      ...EMPTY_LEAK_TEST_PACK,
      ...inputData,
      assetId: asset?.assetId || -1,
      leakTestPackId: parseInt(inputData.leakTestPackId),
    };
    if (defaultValues?.leakTestPackId) leakTestPack.leakTestPackId = defaultValues.leakTestPackId;

    onSubmit(leakTestPack)
      .then(onClose)
      .catch((e) => {
        console.error(e);
      });
  };
  return (
    <Form className="row g-3 px-5" onSubmit={handleSubmit}>
      <div className="col-md-12">
        <FormGroup>
          <Label for="leaktestpack-title">Leak Test Pack Title</Label>
          <Input
            type="text"
            name="leakTestPackTitle"
            id="leaktestpack-title"
            required
            defaultValue={defaultValues?.['leakTestPackTitle'] || ''}
          />
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup>
          <Label for="leakTestPack-type">Type</Label>
          <Input
            type="select"
            name="leakTestPackType"
            id="leakTestPack-type"
            className="form-select-placeholder-hack"
            role="combobox"
            defaultValue={defaultValues?.['leakTestPackType'] || ''}
          >
            <option disabled hidden value="" key="_placeholder_">
              Choose an option
            </option>
            {renderOptions(LEAK_TEST_PACK_TYPE_ENUM)}
          </Input>
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup>
          <Label for="trackPack-number">Leak Test Pack Number</Label>
          <Input
            type="text"
            name="trackPackNumber"
            id="trackPack-number"
            defaultValue={defaultValues?.trackPackNumber || ''}
          />
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup>
          <Label for="system-number">System Number</Label>
          <Input
            type="text"
            name="systemNumber"
            id="system-number"
            defaultValue={defaultValues?.systemNumber || ''}
          />
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup>
          <Label for="spa">SPA</Label>
          <Input type="text" name="spa" id="spa" defaultValue={defaultValues?.spa || ''} />
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup>
          <Label for="ops-review">Ops Review</Label>
          <Input
            type="select"
            name="opsReview"
            id="ops-review"
            className="form-select-placeholder-hack"
            role="combobox"
            defaultValue={defaultValues?.opsReview || 'REQUIRED'}
          >
            {renderOptions(LEAK_TEST_PACK_REVIEW_ENUM)}
          </Input>
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup>
          <Label for="engineering-review">Engineer Review</Label>
          <Input
            type="select"
            name="engineeringReview"
            id="engineering-review"
            className="form-select-placeholder-hack"
            role="combobox"
            defaultValue={defaultValues?.engineeringReview || 'REQUIRED'}
          >
            {renderOptions(LEAK_TEST_PACK_REVIEW_ENUM)}
          </Input>
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup>
          <Label for="asset-review">Asset Review</Label>
          <Input
            type="select"
            name="assetReview"
            id="asset-review"
            className="form-select-placeholder-hack"
            role="combobox"
            defaultValue={defaultValues?.assetReview || 'REQUIRED'}
          >
            {renderOptions(LEAK_TEST_PACK_REVIEW_ENUM)}
          </Input>
        </FormGroup>
      </div>
      <div className="col-md-12">
        <FormGroup>
          <Label for="leakTestPack-status">Status</Label>
          <Input
            type="select"
            name="status"
            id="leakTestPack-status"
            className="form-select-placeholder-hack"
            role="combobox"
            defaultValue={defaultValues?.status || ''}
          >
            <option disabled hidden value="" key="_placeholder_">
              Choose an option
            </option>

            {renderOptions(LEAK_TEST_PACK_STATUS_ENUM)}
          </Input>
        </FormGroup>
      </div>
      <div className="col-md-12">
        <FormGroup>
          <Label for="leakTestPack-comments" className="label-optional">
            Comments
          </Label>
          <Input
            type="textarea"
            name="comments"
            id="leakTestPack-comments"
            defaultValue={defaultValues?.comments || ''}
          />
        </FormGroup>
      </div>
      {type === 'add' ? (
        <div className="col-md-12">
          <Button
            type="submit"
            size="lg"
            className="w-100 my-4"
            theme="darker"
            level="primary"
            disabled={isLoading}
          >
            Add new leak test pack
          </Button>
        </div>
      ) : (
        <div className="row g-3">
          <div className="col-md-6">
            <Button
              size="lg"
              className="w-100 my-4"
              theme="darker"
              level="tertiary"
              onClick={onClose}
            >
              Cancel
            </Button>
          </div>
          <div className="col-md-6">
            <Button
              type="submit"
              size="lg"
              className="w-100 my-4"
              theme="darker"
              level="primary"
              disabled={isLoading}
            >
              Save changes
            </Button>
          </div>
        </div>
      )}
    </Form>
  );
};

export default LeakTestPackForm;
